/**
 * The running timer.
 *
 * Ported from the standalone time-tracker, where this design had already been
 * shaken out. Three properties are load-bearing:
 *
 *  1. TIMESTAMP-BASED, NOT A COUNTER. Only `startedAt` is stored; elapsed is
 *     derived on every tick. A counter drifts, and loses everything to a sleep,
 *     a reload, or a backgrounded tab.
 *  2. MIRRORED TO localStorage. The running timer is restored on the very first
 *     paint, before the server round-trip lands, so a reload never looks like
 *     the timer was lost.
 *  3. RECONCILED ON FOCUS. The server row is the cross-device truth: returning
 *     to the tab adopts a timer started on the phone, or clears one stopped on
 *     the laptop.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";

import { useProjects } from "@/hooks/useData";
import { effectiveRate, useActiveTimer, useClients, useTimeMutations } from "@/hooks/useTime";
import { useToast } from "@/context/ToastContext";
import { defaultKind, fitKind, kindFields, type EntryKind } from "@/lib/billing";
import { appMode } from "@/lib/pocketbase";
import { loadTimerNudge } from "@/lib/theme";

const LOCAL_KEY = "ph.timer.startedAt";
const LOCAL_META = "ph.timer.meta";

/**
 * How long a freshly started local timer is protected from being cleared by a
 * reconcile that hasn't seen it yet. Without this, a window focus in the second
 * after pressing start reads a server row that doesn't exist yet and stops your
 * own timer.
 */
const START_GRACE_MS = 8000;

export interface TimerMeta {
  projectId?: string;
  /** Set directly for ad-hoc work with no project; otherwise derived from one. */
  clientId?: string;
  category?: string;
  itemId?: string;
  note?: string;
  /** The kind chosen when the timer started (lib/billing). Absent means
   *  "whatever the project defaults to" at the moment it stops. */
  kind?: EntryKind;
}

/** A change to the running timer: any of its details, or when it started. */
export type TimerPatch = Partial<TimerMeta> & { startedAt?: string };

interface TimerState {
  running: boolean;
  startedAt: string | null;
  seconds: number;
  meta: TimerMeta;
  start: (meta: TimerMeta) => void;
  /** Edit the running timer without stopping it (the phone's running sheet,
   *  the desktop's running panel). */
  update: (patch: TimerPatch) => void;
  /** `overrides` are last edits made in the same breath as pressing Stop —
   *  applied to this entry even though state has not caught up with them. */
  stop: (overrides?: TimerPatch) => Promise<void>;
  discard: () => Promise<void>;
}

const Ctx = createContext<TimerState | null>(null);

function readLocal<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function TimerProvider({ children }: { children: ReactNode }) {
  const [startedAt, setStartedAt] = useState<string | null>(() => localStorage.getItem(LOCAL_KEY));
  const [meta, setMeta] = useState<TimerMeta>(() => readLocal<TimerMeta>(LOCAL_META, {}));
  const [seconds, setSeconds] = useState(0);
  const lastLocalStart = useRef(0);

  const { data: serverTimer } = useActiveTimer();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { startTimer, updateTimer, clearTimer, logEntry } = useTimeMutations();
  const toast = useToast();

  // Persist locally so a reload restores the timer on first paint.
  useEffect(() => {
    if (startedAt) localStorage.setItem(LOCAL_KEY, startedAt);
    else localStorage.removeItem(LOCAL_KEY);
    localStorage.setItem(LOCAL_META, JSON.stringify(meta));
  }, [startedAt, meta]);

  // Tick. Recomputed from the timestamp rather than incremented, so a sleeping
  // tab catches up the moment it wakes instead of under-counting.
  useEffect(() => {
    if (!startedAt) { setSeconds(0); return; }
    const recompute = () =>
      setSeconds(Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)));
    recompute();
    const id = setInterval(recompute, 1000);
    window.addEventListener("focus", recompute);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", recompute);
    };
  }, [startedAt]);

  // Cross-device reconciliation.
  useEffect(() => {
    if (appMode === "demo") return;
    if (serverTimer) {
      if (serverTimer.startedAt !== startedAt) setStartedAt(serverTimer.startedAt);
      // The activity and a directly picked client live only in this browser
      // (the server row has no column for them), so a refetch keeps them
      // rather than wiping them every time the window regains focus.
      setMeta((m) => ({
        category: m.category,
        clientId: m.clientId,
        projectId: serverTimer.project,
        itemId: serverTimer.item,
        note: serverTimer.note,
        // The server row keeps the two switches; read them back as a kind.
        kind: serverTimer.internal ? "internal" : serverTimer.billable === false ? "no_charge" : m.kind,
      }));
    } else if (startedAt && Date.now() - lastLocalStart.current > START_GRACE_MS) {
      // No server row and we didn't just start one: it was stopped elsewhere.
      setStartedAt(null);
      setMeta({});
    }
  }, [serverTimer]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback((next: TimerMeta) => {
    const now = new Date().toISOString();
    lastLocalStart.current = Date.now();
    setStartedAt(now);
    setMeta(next);
    if (appMode !== "demo") {
      startTimer.mutate({
        startedAt: now,
        projectId: next.projectId,
        itemId: next.itemId,
        note: next.note,
        ...(next.kind ? kindFields(next.kind, null) : {}),
      });
    }
  }, [startTimer]);

  const update = useCallback((patch: TimerPatch) => {
    if (!startedAt) return;
    const { startedAt: nextStart, ...rest } = patch;
    // Treat it like a fresh start for the reconcile's grace period: the
    // server row is about to change, and a focus refetch in between would
    // otherwise put the old values back.
    lastLocalStart.current = Date.now();
    if (nextStart) setStartedAt(nextStart);
    setMeta((m) => ({ ...m, ...rest }));
    if (appMode !== "demo") {
      updateTimer.mutate({
        ...(nextStart ? { startedAt: nextStart } : {}),
        ...("projectId" in rest ? { projectId: rest.projectId } : {}),
        ...("itemId" in rest ? { itemId: rest.itemId } : {}),
        ...(rest.note !== undefined ? { note: rest.note } : {}),
        ...(rest.kind ? kindFields(rest.kind, null) : {}),
      });
    }
  }, [startedAt, updateTimer]);

  const stop = useCallback(async (overrides?: TimerPatch) => {
    if (!startedAt) return;
    const { startedAt: overrideStart, ...metaOverrides } = overrides ?? {};
    const began = overrideStart ?? startedAt;
    const final = { ...meta, ...metaOverrides };
    const end = new Date();
    const durationSeconds = Math.max(0, Math.floor((end.getTime() - new Date(began).getTime()) / 1000));
    const { projectId } = final;

    // Clear the UI first: the entry write can fail, but leaving a timer visibly
    // running after the user pressed stop is worse than an error toast.
    setStartedAt(null);
    setMeta({});
    if (appMode !== "demo") await clearTimer.mutateAsync();

    // A sub-second press is a misclick, not work. A missing project is not —
    // ad-hoc time is a first-class entry.
    if (durationSeconds < 1) return;

    const project = projects.find((p) => p.id === projectId);
    const derived = effectiveRate(project, clients);
    // With no project there is no rate to derive from it, so fall back to the
    // client picked directly on the timer.
    const directClient = final.clientId ? clients.find((c) => c.id === final.clientId) : undefined;
    const rate = derived.rate ?? directClient?.defaultRate ?? null;
    const currency = derived.currency || directClient?.currency || "USD";
    const clientId = derived.clientId ?? directClient?.id ?? null;
    // The project may have changed type while the timer ran; keep the choice
    // only if it is still on offer there.
    const chosen = final.kind ?? defaultKind(project, !!clientId);
    const fields = kindFields(fitKind(chosen, project, !!clientId), rate);

    // Callers fire this from onClick as `void timer.stop()`, which swallows a
    // rejection — so a failed write would look exactly like a successful one
    // and the time would just be gone. Report it here instead.
    try {
      await logEntry.mutateAsync({
        projectId,
        category: final.category,
        itemId: final.itemId,
        start: began,
        end: end.toISOString(),
        durationSeconds,
        note: final.note ?? "",
        billable: fields.billable, internal: fields.internal,
        source: "timer",
        rate: fields.rate, currency, clientId,
      });
    } catch (err) {
      toast(`Timer stopped but the entry did not save — ${err instanceof Error ? err.message : "unknown error"}`);
    }
  }, [startedAt, meta, projects, clients, clearTimer, logEntry, toast]);

  const discard = useCallback(async () => {
    setStartedAt(null);
    setMeta({});
    if (appMode !== "demo") await clearTimer.mutateAsync();
  }, [clearTimer]);

  // The long-running nudge. Keyed to the timer's start so a new timer starts
  // clean, and to the multiple so each threshold fires once — not every tick.
  // Read fresh each tick so a change in Settings applies to a running timer.
  const nudged = useRef<{ startedAt: string | null; step: number }>({ startedAt: null, step: 0 });
  useEffect(() => {
    const threshold = Number(loadTimerNudge()) * 3600;
    if (!startedAt || !threshold) return;
    if (nudged.current.startedAt !== startedAt) nudged.current = { startedAt, step: 0 };
    const step = Math.floor(seconds / threshold);
    if (step < 1 || step <= nudged.current.step) return;
    nudged.current.step = step;

    const what = projects.find((p) => p.id === meta.projectId)?.name ?? meta.category ?? "ad-hoc work";
    const msg = `Timer has run ${Math.floor(seconds / 3600)}h on ${what} — still going?`;
    toast(msg, { ms: 15000 });
    // A toast only helps if the tab is in front of you. When it is not, and
    // you allowed notifications in Settings, say it where you will see it.
    if (document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
      try { new Notification("Project Hub", { body: msg, tag: "ph-timer-nudge" }); } catch { /* iOS without install */ }
    }
  }, [seconds, startedAt, meta, projects, toast]);

  const value = useMemo<TimerState>(
    () => ({ running: startedAt !== null, startedAt, seconds, meta, start, update, stop, discard }),
    [startedAt, seconds, meta, start, update, stop, discard],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTimer(): TimerState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTimer must be used inside <TimerProvider>");
  return ctx;
}

/** "1:04:09" / "7:32" — the running display, never rounded. */
export function formatElapsed(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(h ? 2 : 1, "0");
  return `${h ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}
